import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import { Button } from "@/shared/components/ui/button";
import { Badge } from "@/shared/components/ui/badge";
import {
  Edit,
  Trash2,
  Image as ImageIcon,
  ChevronLeft,
  ChevronRight,
  CalendarDays,
} from "lucide-react";
import type { StockResponseDTO } from "@/features/stocks/types/stock.types";
import { StockStatus } from "@/features/stocks/types/stock.types";
import { Can } from "@/features/auth/components/Can";
import { ACTIONS } from "@/constants/roles";
import {
  getStockConditionLabel,
  getStockStatusLabel,
  getStockStatusClass,
} from "@/features/stocks/utils/stock.utils";
import { memo } from "react";

interface StocksTableProps {
  stocks: StockResponseDTO[];
  stockStatuses: Map<number, StockStatus>;
  loading: boolean;
  currentPage: number;
  totalPages: number;
  onEdit: (stock: StockResponseDTO) => void;
  onDelete: (stock: StockResponseDTO) => void;
  onViewImage: (stock: StockResponseDTO) => void;
  onViewCalendar: (stock: StockResponseDTO) => void;
  onPageChange: (page: number) => void;
}

export const StocksTable = memo(function StocksTable({
  stocks,
  stockStatuses,
  loading,
  currentPage,
  totalPages,
  onEdit,
  onDelete,
  onViewImage,
  onViewCalendar,
  onPageChange,
}: StocksTableProps) {
  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Produto</TableHead>
            <TableHead>Código de Barras</TableHead>
            <TableHead>Tamanho</TableHead>
            <TableHead>Condição</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Foto</TableHead>
            <TableHead className="text-right">Ações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {stocks.map((stock) => {
            const status = stockStatuses.get(stock.id);
            return (
              <TableRow key={stock.id}>
                <TableCell className="font-medium">
                  {stock.productName}
                </TableCell>
                <TableCell>
                  <Badge variant="outline">{stock.barcode}</Badge>
                </TableCell>
                <TableCell>
                  <Badge variant="secondary">{stock.size}</Badge>
                </TableCell>
                <TableCell>{getStockConditionLabel(stock.condition)}</TableCell>
                <TableCell>
                  {status ? (
                    <Badge
                      variant="outline"
                      className={getStockStatusClass(status)}
                    >
                      {getStockStatusLabel(status)}
                    </Badge>
                  ) : (
                    <span className="text-muted-foreground text-sm">
                      Carregando...
                    </span>
                  )}
                </TableCell>
                <TableCell>
                  {stock.photoUrl ? (
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      onClick={() => onViewImage(stock)}
                      title="Ver foto"
                      disabled={loading}
                    >
                      <ImageIcon className="w-4 h-4 text-primary" />
                    </Button>
                  ) : (
                    <span className="text-muted-foreground text-sm">-</span>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      onClick={() => onViewCalendar(stock)}
                      title="Ver disponibilidade"
                      disabled={loading}
                    >
                      <CalendarDays className="w-4 h-4" />
                    </Button>
                    <Can permission={ACTIONS.stock.update}>
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        onClick={() => onEdit(stock)}
                        title="Editar"
                        disabled={loading}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                    </Can>
                    <Can permission={ACTIONS.stock.delete}>
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        onClick={() => onDelete(stock)}
                        title="Excluir"
                        disabled={loading}
                      >
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </Can>
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      {totalPages > 1 && (
        <div className="flex flex-col md:flex-row items-center justify-between mt-4 pt-4 border-t gap-4">
          <div className="text-sm text-muted-foreground">
            Página {currentPage + 1} de {totalPages}
          </div>
          <div className="flex gap-2 items-center flex-wrap justify-center">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 0 || loading}
            >
              <ChevronLeft className="w-4 h-4" />
              Anterior
            </Button>

            <div className="flex gap-1 flex-wrap justify-center">
              {Array.from({ length: totalPages }, (_, i) => {
                const showPage =
                  i === 0 ||
                  i === totalPages - 1 ||
                  (i >= currentPage - 1 && i <= currentPage + 1);

                const showEllipsis =
                  (i === 1 && currentPage > 2) ||
                  (i === totalPages - 2 && currentPage < totalPages - 3);

                if (showEllipsis) {
                  return (
                    <span
                      key={i}
                      className="px-2 py-1 text-sm text-muted-foreground"
                    >
                      ...
                    </span>
                  );
                }

                if (!showPage) return null;

                return (
                  <Button
                    key={i}
                    variant={currentPage === i ? "default" : "outline"}
                    size="sm"
                    onClick={() => onPageChange(i)}
                    disabled={loading}
                    className="min-w-10"
                  >
                    {i + 1}
                  </Button>
                );
              })}
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage === totalPages - 1 || loading}
            >
              Próxima
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </>
  );
});
